// ============================================================================
// Form W-4 Employee's Withholding Certificate (2026)
// Federal Tax Withholding Form with all steps and worksheets
// Complete IRS PDF-compatible version with PDF generation
// Updated for 2026 tax year with new deductions
// ============================================================================

import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  FileText,
  Check,
  AlertTriangle,
  Calculator,
  DollarSign,
  User,
  Users,
  Baby,
  HelpCircle,
  Info,
  ChevronRight,
  Save,
  Send,
  CheckCircle2,
  Download,
  Building2,
  Printer,
  ShieldOff,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import ESignature, { SignatureDisplay } from "../compliance/ESignature";
import { W4EmployerSection } from "./W4EmployerSection";
import { toast } from "sonner";
import {
  mapFormDataToPdfFields,
  mapFormDataToTemplateFields,
  calculateDeductionsWorksheet,
  getInitialFormData,
  TAX_YEAR,
  CHILD_TAX_CREDIT,
  OTHER_DEPENDENT_CREDIT,
  STANDARD_DEDUCTIONS,
  PHASE_OUT_THRESHOLDS,
  DEDUCTION_LIMITS,
} from "@/lib/w4FieldMapping";

// ============================================================================
// 2026 TAX YEAR CONSTANTS (imported from w4FieldMapping.js)
// Additional local constants below
// ============================================================================

// Income limits for credits (local only)
const INCOME_LIMITS = {
  single: 200000,
  married_jointly: 400000,
  head_of_household: 200000,
};

// US States for address dropdown
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

// ============================================================================
// W-4 FILING STATUS OPTIONS (2026)
// ============================================================================

const W4_FILING_STATUS = [
  {
    value: "single",
    label: "Single or Married filing separately",
    description:
      "Choose this if you are single, divorced, or married but file a separate tax return",
  },
  {
    value: "married_jointly",
    label: "Married filing jointly or Qualifying surviving spouse",
    description:
      "Choose this if you are married and file a joint return with your spouse, or if you are a qualifying surviving spouse",
  },
  {
    value: "head_of_household",
    label: "Head of household",
    description:
      "Check only if you're unmarried and pay more than half the costs of keeping up a home for yourself and a qualifying individual",
  },
];

// Pay period options for Multiple Jobs Worksheet
const PAY_PERIODS = [
  { value: 52, label: "Weekly" },
  { value: 26, label: "Every other week (Bi-weekly)" },
  { value: 24, label: "Twice a month (Semi-monthly)" },
  { value: 12, label: "Monthly" },
  { value: 4, label: "Quarterly" },
  { value: 1, label: "Annually" },
];

// ============================================================================
// FORM FIELD COMPONENT
// ============================================================================

function FormField({ label, required, error, children, helpText, tooltip }) {
  return (
    <div className="space-y-1">
      <Label className="flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-3 w-3 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Label>
      {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ============================================================================
// CURRENCY INPUT COMPONENT
// ============================================================================

function CurrencyInput({ value, onChange, placeholder, disabled }) {
  const formatValue = (val) => {
    if (!val) return "";
    const num = parseFloat(val.toString().replace(/[^0-9.]/g, ""));
    if (isNaN(num)) return "";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.]/g, "");
    onChange(rawValue ? parseFloat(rawValue) : 0);
  };

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
        $
      </span>
      <Input
        value={formatValue(value)}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className="pl-7"
      />
    </div>
  );
}

// ============================================================================
// STEP 1: PERSONAL INFORMATION
// ============================================================================

function W4Step1({ formData, onChange, errors = {}, disabled = false }) {
  const handleChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <User className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <CardTitle className="text-base">
              Step 1: Enter Personal Information
            </CardTitle>
            <CardDescription>Required for all employees</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FormField label="First Name" required error={errors.firstName}>
            <Input
              value={formData.firstName || ""}
              onChange={(e) => handleChange("firstName", e.target.value)}
              placeholder="First name"
              disabled={disabled}
            />
          </FormField>
          <FormField label="Middle Initial">
            <Input
              value={formData.middleInitial || ""}
              onChange={(e) =>
                handleChange(
                  "middleInitial",
                  e.target.value.slice(0, 2).toUpperCase(),
                )
              }
              placeholder="M.I."
              maxLength={2}
              disabled={disabled}
              className="w-20"
            />
          </FormField>
          <FormField label="Last Name" required error={errors.lastName}>
            <Input
              value={formData.lastName || ""}
              onChange={(e) => handleChange("lastName", e.target.value)}
              placeholder="Last name"
              disabled={disabled}
            />
          </FormField>
          <FormField
            label="Social Security Number"
            required
            error={errors.ssn}
            tooltip="Your SSN is required for tax withholding purposes"
          >
            <Input
              value={formData.ssn || ""}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 9);
                const formatted = value.replace(
                  /(\d{3})(\d{2})(\d{4})/,
                  "$1-$2-$3",
                );
                handleChange("ssn", formatted);
              }}
              placeholder="XXX-XX-XXXX"
              disabled={disabled}
            />
          </FormField>
        </div>

        {/* Address Fields - Separate for PDF mapping */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700">Address</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Street Address" required error={errors.address}>
              <Input
                value={formData.address || ""}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="Street number and name"
                disabled={disabled}
              />
            </FormField>
            <FormField label="Apartment/Suite/Unit">
              <Input
                value={formData.addressLine2 || ""}
                onChange={(e) => handleChange("addressLine2", e.target.value)}
                placeholder="Apt, Suite, Unit (optional)"
                disabled={disabled}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <FormField label="City" required error={errors.city}>
                <Input
                  value={formData.city || ""}
                  onChange={(e) => handleChange("city", e.target.value)}
                  placeholder="City"
                  disabled={disabled}
                />
              </FormField>
            </div>
            <FormField label="State" required error={errors.state}>
              <Select
                value={formData.state || ""}
                onValueChange={(value) => handleChange("state", value)}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {US_STATES.map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.code} - {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="ZIP Code" required error={errors.zipCode}>
              <Input
                value={formData.zipCode || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 9);
                  // Format as 12345-6789 if more than 5 digits
                  const formatted =
                    value.length > 5
                      ? `${value.slice(0, 5)}-${value.slice(5)}`
                      : value;
                  handleChange("zipCode", formatted);
                }}
                placeholder="12345"
                disabled={disabled}
              />
            </FormField>
          </div>
        </div>

        {/* Filing Status */}
        <div className="space-y-3">
          <Label className="flex items-center gap-1">
            Filing Status <span className="text-red-500">*</span>
          </Label>
          <p className="text-xs text-gray-500">
            Check only one box. Your withholding depends on your filing status.
          </p>

          <RadioGroup
            value={formData.filingStatus || ""}
            onValueChange={(value) => handleChange("filingStatus", value)}
            disabled={disabled}
            className="space-y-3"
          >
            {W4_FILING_STATUS.map((status) => (
              <div
                key={status.value}
                className={`flex items-start space-x-3 p-4 rounded-lg border transition-colors ${
                  formData.filingStatus === status.value
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <RadioGroupItem value={status.value} id={status.value} />
                <Label htmlFor={status.value} className="flex-1 cursor-pointer">
                  <span className="font-medium">{status.label}</span>
                  <p className="text-xs text-gray-500 mt-1">
                    {status.description}
                  </p>
                </Label>
              </div>
            ))}
          </RadioGroup>
          {errors.filingStatus && (
            <p className="text-xs text-red-500">{errors.filingStatus}</p>
          )}
        </div>

        {/* Exemption from Withholding */}
        <div className="border rounded-lg p-4 space-y-3 bg-red-50/30">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-red-300 text-red-700">
              Exemption
            </Badge>
            <span className="font-medium">Exemption from withholding</span>
          </div>

          <p className="text-sm text-gray-600">
            You may claim exemption from withholding for {TAX_YEAR} if you meet{" "}
            <strong>both</strong> of the following conditions: you had no
            federal income tax liability in {TAX_YEAR - 1} and you expect to
            have no federal income tax liability in {TAX_YEAR}.
          </p>

          <div className="flex items-start space-x-3 p-3 border rounded-lg bg-white">
            <Checkbox
              id="claimExempt"
              checked={formData.claimExempt || false}
              onCheckedChange={(checked) =>
                handleChange("claimExempt", checked)
              }
              disabled={disabled}
            />
            <Label htmlFor="claimExempt" className="cursor-pointer text-sm">
              I claim exemption from withholding for {TAX_YEAR}, and I certify
              that I meet <strong>both</strong> of the conditions above for
              exemption.
            </Label>
          </div>

          {formData.claimExempt && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800 text-sm">
                <strong>Important:</strong> If you claim exemption, you must
                write "Exempt" on line 4(c), skip Steps 2-4, and complete Step
                5. Your employer will not withhold federal income tax from your
                paychecks. Note: A new W-4 claiming exemption must be filed by
                February 15, {TAX_YEAR + 1}.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// STEP 2: MULTIPLE JOBS OR SPOUSE WORKS (2026)
// ============================================================================

// 2026 Higher Paying Job Annual Taxable Wage & Salary Table
const MULTIPLE_JOBS_TABLE = {
  married_jointly: {
    // Ranges: [min, max, ...adjustment values for lower wages]
    ranges: [
      {
        min: 0,
        max: 14999,
        cols: [0, 0, 380, 1260, 1410, 1540, 1680, 1870, 2050, 2220, 2390, 2560],
      },
      {
        min: 15000,
        max: 29999,
        cols: [
          380, 380, 1260, 2140, 2290, 2420, 2560, 2750, 2930, 3100, 3270, 3440,
        ],
      },
      {
        min: 30000,
        max: 49999,
        cols: [
          1260, 1260, 2140, 3020, 3170, 3300, 3440, 3630, 3810, 3980, 4150,
          4320,
        ],
      },
      {
        min: 50000,
        max: 69999,
        cols: [
          1410, 1410, 2290, 3170, 3320, 3450, 3590, 3780, 3960, 4130, 4300,
          4470,
        ],
      },
      {
        min: 70000,
        max: 89999,
        cols: [
          1540, 1540, 2420, 3300, 3450, 3580, 3720, 3910, 4090, 4260, 4430,
          4600,
        ],
      },
      {
        min: 90000,
        max: 109999,
        cols: [
          1680, 1680, 2560, 3440, 3590, 3720, 3860, 4050, 4230, 4400, 4570,
          4740,
        ],
      },
      {
        min: 110000,
        max: 149999,
        cols: [
          1870, 1870, 2750, 3630, 3780, 3910, 4050, 4240, 4420, 4590, 4760,
          4930,
        ],
      },
      {
        min: 150000,
        max: 199999,
        cols: [
          2050, 2050, 2930, 3810, 3960, 4090, 4230, 4420, 4600, 4770, 4940,
          5110,
        ],
      },
      {
        min: 200000,
        max: 274999,
        cols: [
          2220, 2220, 3100, 3980, 4130, 4260, 4400, 4590, 4770, 4940, 5110,
          5280,
        ],
      },
      {
        min: 275000,
        max: 374999,
        cols: [
          2390, 2390, 3270, 4150, 4300, 4430, 4570, 4760, 4940, 5110, 5280,
          5450,
        ],
      },
      {
        min: 375000,
        max: 474999,
        cols: [
          2560, 2560, 3440, 4320, 4470, 4600, 4740, 4930, 5110, 5280, 5450,
          5620,
        ],
      },
      {
        min: 475000,
        max: Infinity,
        cols: [
          2730, 2730, 3610, 4490, 4640, 4770, 4910, 5100, 5280, 5450, 5620,
          5790,
        ],
      },
    ],
    lowerWageRanges: [
      { min: 0, max: 14999 },
      { min: 15000, max: 29999 },
      { min: 30000, max: 49999 },
      { min: 50000, max: 69999 },
      { min: 70000, max: 89999 },
      { min: 90000, max: 109999 },
      { min: 110000, max: 149999 },
      { min: 150000, max: 199999 },
      { min: 200000, max: 274999 },
      { min: 275000, max: 374999 },
      { min: 375000, max: 474999 },
      { min: 475000, max: Infinity },
    ],
  },
  single: {
    ranges: [
      {
        min: 0,
        max: 14999,
        cols: [660, 1080, 1360, 1480, 1610, 1750, 1940, 2120, 2290],
      },
      {
        min: 15000,
        max: 24999,
        cols: [1080, 1500, 1780, 1900, 2030, 2170, 2360, 2540, 2710],
      },
      {
        min: 25000,
        max: 34999,
        cols: [1360, 1780, 2060, 2180, 2310, 2450, 2640, 2820, 2990],
      },
      {
        min: 35000,
        max: 44999,
        cols: [1480, 1900, 2180, 2300, 2430, 2570, 2760, 2940, 3110],
      },
      {
        min: 45000,
        max: 54999,
        cols: [1610, 2030, 2310, 2430, 2560, 2700, 2890, 3070, 3240],
      },
      {
        min: 55000,
        max: 74999,
        cols: [1750, 2170, 2450, 2570, 2700, 2840, 3030, 3210, 3380],
      },
      {
        min: 75000,
        max: 99999,
        cols: [1940, 2360, 2640, 2760, 2890, 3030, 3220, 3400, 3570],
      },
      {
        min: 100000,
        max: 149999,
        cols: [2120, 2540, 2820, 2940, 3070, 3210, 3400, 3580, 3750],
      },
      {
        min: 150000,
        max: Infinity,
        cols: [2290, 2710, 2990, 3110, 3240, 3380, 3570, 3750, 3920],
      },
    ],
    lowerWageRanges: [
      { min: 0, max: 14999 },
      { min: 15000, max: 24999 },
      { min: 25000, max: 34999 },
      { min: 35000, max: 44999 },
      { min: 45000, max: 54999 },
      { min: 55000, max: 74999 },
      { min: 75000, max: 99999 },
      { min: 100000, max: 149999 },
      { min: 150000, max: Infinity },
    ],
  },
};
// head_of_household uses same table as single
MULTIPLE_JOBS_TABLE.head_of_household = MULTIPLE_JOBS_TABLE.single;

// Helper to look up table value
function lookupMultipleJobsTableValue(filingStatus, higherWages, lowerWages) {
  const table = MULTIPLE_JOBS_TABLE[filingStatus] || MULTIPLE_JOBS_TABLE.single;

  // Auto-swap if values are entered in wrong order (higher should be larger)
  let actualHigher = higherWages;
  let actualLower = lowerWages;
  if (lowerWages > higherWages) {
    actualHigher = lowerWages;
    actualLower = higherWages;
    console.log(
      "🔄 Swapping wages - higher:",
      actualHigher,
      "lower:",
      actualLower,
    );
  }

  // Debug logging
  console.log("📊 Multiple Jobs Lookup:", {
    filingStatus,
    originalHigherWages: higherWages,
    originalLowerWages: lowerWages,
    actualHigher,
    actualLower,
  });

  // Find higher wage row
  const higherRow = table.ranges.find(
    (r) => actualHigher >= r.min && actualHigher <= r.max,
  );
  if (!higherRow) {
    console.log("❌ No matching row for higherWages:", actualHigher);
    return 0;
  }

  // Find lower wage column index
  const lowerColIndex = table.lowerWageRanges.findIndex(
    (r) => actualLower >= r.min && actualLower <= r.max,
  );
  if (lowerColIndex === -1) {
    console.log("❌ No matching column for lowerWages:", actualLower);
    return 0;
  }

  const result = higherRow.cols[lowerColIndex];
  console.log("✅ Lookup result:", {
    rowRange: `${higherRow.min}-${higherRow.max}`,
    lowerRange: table.lowerWageRanges[lowerColIndex],
    colIndex: lowerColIndex,
    result,
  });

  return result;
}

// ============================================================================
// STEP 2: MULTIPLE JOBS OR SPOUSE WORKS (2026)
// ============================================================================

// [Keep MULTIPLE_JOBS_TABLE and lookup function as is - they are external to the component]

function W4Step2({ formData, onChange, errors = {}, disabled = false }) {
  const handleChange = (field, value) => {
    onChange({ ...formData, step2: { ...formData.step2, [field]: value } });
  };

  const step2Data = formData.step2 || {};
  const filingStatus = formData.filingStatus || "single";
  const [showWorksheet, setShowWorksheet] = useState(false);
  const [hasMultipleJobs, setHasMultipleJobs] = useState(
    step2Data.usedEstimator ||
      step2Data.twoJobsOnly ||
      step2Data.hasThreeOrMoreJobs,
  );

  // Sync internal toggle if data exists
  useEffect(() => {
    if (
      step2Data.usedEstimator ||
      step2Data.twoJobsOnly ||
      step2Data.hasThreeOrMoreJobs
    ) {
      setHasMultipleJobs(true);
    }
  }, [step2Data]);

  // Calculate Multiple Jobs Worksheet
  const calculateWorksheet = () => {
    const hasThreeJobs = step2Data.hasThreeOrMoreJobs;

    console.log("🔢 calculateWorksheet called:", {
      hasThreeJobs,
      filingStatus,
      step2Data,
    });

    if (hasThreeJobs) {
      // Three+ jobs: Line 2a, 2b, 2c approach
      const wages1 = parseFloat(step2Data.job1Wages) || 0;
      const wages2 = parseFloat(step2Data.job2Wages) || 0;
      const wages3 = parseFloat(step2Data.job3Wages) || 0;

      // Sort to get highest and second highest
      const sortedWages = [wages1, wages2, wages3].sort((a, b) => b - a);
      const highest = sortedWages[0];
      const secondHighest = sortedWages[1];
      const third = sortedWages[2];

      // 2a: Look up two highest in table
      const line2a = lookupMultipleJobsTableValue(
        filingStatus,
        highest,
        secondHighest,
      );

      // 2b: Add line 2a + two highest wages
      const line2b = line2a + highest + secondHighest;

      // 2c: Look up line 2b result against third job
      const line2c = lookupMultipleJobsTableValue(filingStatus, line2b, third);

      return { line1: 0, line2a, line2b, line2c, line3: line2c };
    } else {
      // Two jobs: Simple lookup
      const higherWages = parseFloat(step2Data.higherJobWages) || 0;
      const lowerWages = parseFloat(step2Data.lowerJobWages) || 0;

      console.log("💼 Two jobs calculation:", { higherWages, lowerWages });

      const line1 = lookupMultipleJobsTableValue(
        filingStatus,
        higherWages,
        lowerWages,
      );

      console.log("📝 Line 1 result:", line1);

      return { line1, line2a: 0, line2b: 0, line2c: 0, line3: line1 };
    }
  };

  const calcPayPeriods = () => {
    const line3 = wsCalc.line3;
    const payPeriods = parseInt(step2Data.payPeriodsPerYear) || 26;
    return payPeriods > 0 ? Math.round(line3 / payPeriods) : 0;
  };

  const wsCalc = calculateWorksheet();
  const line4Result = calcPayPeriods();

  // Update step2 data with calculated worksheet values whenever they change
  useEffect(() => {
    const newStep2 = {
      ...step2Data,
      twoJobsAmount: wsCalc.line1,
      line2a: wsCalc.line2a,
      line2b: wsCalc.line2b,
      line2c: wsCalc.line2c,
      line4Result: line4Result,
    };
    // Only update if values changed to prevent infinite loop
    if (
      step2Data.twoJobsAmount !== wsCalc.line1 ||
      step2Data.line2a !== wsCalc.line2a ||
      step2Data.line2b !== wsCalc.line2b ||
      step2Data.line2c !== wsCalc.line2c ||
      step2Data.line4Result !== line4Result
    ) {
      onChange({ ...formData, step2: newStep2 });
    }
  }, [wsCalc.line1, wsCalc.line2a, wsCalc.line2b, wsCalc.line2c, line4Result]);

  const handleApplyResult = () => {
    onChange({
      ...formData,
      step2: {
        ...step2Data,
        twoJobsAmount: wsCalc.line1,
        line2a: wsCalc.line2a,
        line2b: wsCalc.line2b,
        line2c: wsCalc.line2c,
        line4Result: line4Result,
      },
      step4: { ...(formData.step4 || {}), extraWithholding: line4Result },
    });
    toast.success("Calculated amount applied to Step 4(c)");
  };

  return (
    <Card className="border-gray-200 shadow-none">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
            <Users className="h-4 w-4 text-orange-600" />
          </div>
          <div>
            <CardTitle className="text-base">
              Step 2: Multiple Jobs or Spouse Works
            </CardTitle>
            <CardDescription>
              Complete this step ONLY if you have multiple jobs or are married
              filing jointly and your spouse works.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Toggle */}
        <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
          <div className="space-y-0.5">
            <Label className="text-base font-medium">
              Do you have multiple jobs or a working spouse?
            </Label>
            <p className="text-sm text-gray-500">
              Withholding is most accurate if you do this on the W-4 for the
              highest paying job.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex gap-4">
              <Button
                variant={hasMultipleJobs ? "default" : "outline"}
                onClick={() => setHasMultipleJobs(true)}
                size="sm"
              >
                Yes
              </Button>
              <Button
                variant={!hasMultipleJobs ? "secondary" : "ghost"}
                onClick={() => {
                  setHasMultipleJobs(false);
                  handleChange("twoJobsOnly", false);
                  handleChange("usedEstimator", false);
                }}
                size="sm"
              >
                No
              </Button>
            </div>
          </div>
        </div>

        {hasMultipleJobs && (
          <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
            <Label className="text-sm font-semibold text-gray-900">
              Select ONLY ONE of these options:
            </Label>

            {/* Option C - Checkbox (Simplest) */}
            <div
              className={`p-4 border rounded-lg transition-colors cursor-pointer ${
                step2Data.twoJobsOnly
                  ? "border-blue-500 bg-blue-50/20"
                  : "hover:border-gray-300"
              }`}
              onClick={() =>
                handleChange("twoJobsOnly", !step2Data.twoJobsOnly)
              }
            >
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="twoJobsOnly"
                  checked={step2Data.twoJobsOnly || false}
                  onCheckedChange={(checked) =>
                    handleChange("twoJobsOnly", checked)
                  }
                  disabled={disabled}
                  className="mt-1"
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="twoJobsOnly"
                    className="font-medium cursor-pointer"
                  >
                    Option 1: Check this box if there are only two jobs total
                  </Label>
                  <p className="text-sm text-gray-500">
                    Use this if the pay at both jobs is similar.{" "}
                    <strong>
                      Do not use this if you used the estimator or worksheet.
                    </strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Option A - Estimator */}
            <div className="p-4 border rounded-lg">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="font-medium">
                    Option 2: Use the IRS Estimator (Most Accurate)
                  </Label>
                  <Badge variant="outline" className="text-xs">
                    Recommended
                  </Badge>
                </div>
                <p className="text-sm text-gray-500">
                  Go to{" "}
                  <a
                    href="https://www.irs.gov/W4app"
                    target="_blank"
                    className="text-blue-600 underline"
                  >
                    www.irs.gov/W4app
                  </a>
                  , then enter the result in Step 4(c) below.
                </p>
              </div>
            </div>

            {/* Option B - Worksheet (Complex) */}
            <div className="border rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                onClick={() => setShowWorksheet(!showWorksheet)}
              >
                <div>
                  <span className="font-medium block">
                    Option 3: Use the Multiple Jobs Worksheet
                  </span>
                  <span className="text-sm text-gray-500">
                    Use this if you can't use the estimator and have privacy
                    concerns
                  </span>
                </div>
                <ChevronRight
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    showWorksheet ? "rotate-90" : ""
                  }`}
                />
              </button>

              {showWorksheet && (
                <div className="p-4 space-y-6 border-t bg-white">
                  <div className="text-sm font-medium text-gray-900 border-b pb-2">
                    Multiple Jobs Worksheet (Page 3 of Form W-4)
                  </div>

                  {/* Job count selection */}
                  <div className="space-y-3">
                    <Label>How many total jobs?</Label>
                    <RadioGroup
                      value={step2Data.hasThreeOrMoreJobs ? "three" : "two"}
                      onValueChange={(val) =>
                        handleChange("hasThreeOrMoreJobs", val === "three")
                      }
                      disabled={disabled}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="two" id="twoJobs" />
                        <Label htmlFor="twoJobs">Two jobs</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="three" id="threeJobs" />
                        <Label htmlFor="threeJobs">Three or more jobs</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {!step2Data.hasThreeOrMoreJobs ? (
                    // TWO JOBS workflow - Line 1
                    <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <Label className="text-xs font-semibold uppercase text-blue-700 block">
                        Line 1: Two Jobs Calculation
                      </Label>
                      <p className="text-xs text-blue-600 mb-2">
                        Find the amount from the table using higher paying job
                        row and lower paying job column.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-gray-600 mb-1.5 block">
                            Higher paying job annual wages
                          </Label>
                          <CurrencyInput
                            value={step2Data.higherJobWages}
                            onChange={(val) =>
                              handleChange("higherJobWages", val)
                            }
                            placeholder="$0"
                            disabled={disabled}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 mb-1.5 block">
                            Lower paying job annual wages
                          </Label>
                          <CurrencyInput
                            value={step2Data.lowerJobWages}
                            onChange={(val) =>
                              handleChange("lowerJobWages", val)
                            }
                            placeholder="$0"
                            disabled={disabled}
                          />
                        </div>
                      </div>
                      {/* Filing status indicator */}
                      <div className="text-xs text-blue-600 bg-blue-100 p-2 rounded">
                        Calculating based on filing status:{" "}
                        <strong>
                          {filingStatus === "married_jointly"
                            ? "Married filing jointly"
                            : filingStatus === "head_of_household"
                              ? "Head of household"
                              : "Single"}
                        </strong>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                        <span className="text-xs font-medium text-blue-700">
                          Line 1 (Table lookup result):
                        </span>
                        <span className="text-lg font-bold text-blue-700">
                          ${wsCalc.line1.toLocaleString()}
                        </span>
                      </div>
                      {/* Explanation when result is $0 */}
                      {wsCalc.line1 === 0 &&
                        (parseFloat(step2Data.higherJobWages) > 0 ||
                          parseFloat(step2Data.lowerJobWages) > 0) && (
                          <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
                            {filingStatus === "married_jointly" &&
                            parseFloat(step2Data.higherJobWages || 0) < 30000 &&
                            parseFloat(step2Data.lowerJobWages || 0) < 30000
                              ? "For married filing jointly with both wages under $30,000, the IRS table indicates $0 additional withholding is needed."
                              : parseFloat(step2Data.higherJobWages || 0) <
                                    15000 &&
                                  parseFloat(step2Data.lowerJobWages || 0) <
                                    15000
                                ? "Note: The amounts entered are very low annual wages. If you meant thousands, please add the full amount (e.g., $50,000 instead of $50)."
                                : "Based on your wage amounts and filing status, no additional withholding adjustment is needed."}
                          </p>
                        )}
                    </div>
                  ) : (
                    // THREE+ JOBS workflow - Lines 2a, 2b, 2c
                    <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <Label className="text-xs font-semibold uppercase text-purple-700 block">
                        Lines 2a-2c: Three or More Jobs Calculation
                      </Label>
                      <p className="text-xs text-purple-600 mb-2">
                        Enter annual wages for each job (highest to lowest):
                      </p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-gray-600 mb-1.5 block">
                            Job 1 (Highest)
                          </Label>
                          <CurrencyInput
                            value={step2Data.job1Wages}
                            onChange={(v) => handleChange("job1Wages", v)}
                            placeholder="$0"
                            disabled={disabled}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 mb-1.5 block">
                            Job 2 (Second)
                          </Label>
                          <CurrencyInput
                            value={step2Data.job2Wages}
                            onChange={(v) => handleChange("job2Wages", v)}
                            placeholder="$0"
                            disabled={disabled}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 mb-1.5 block">
                            Job 3 (Third)
                          </Label>
                          <CurrencyInput
                            value={step2Data.job3Wages}
                            onChange={(v) => handleChange("job3Wages", v)}
                            placeholder="$0"
                            disabled={disabled}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t border-purple-200">
                        <div className="bg-white p-2 rounded">
                          <span className="text-purple-600">
                            2a. Table lookup (Jobs 1 & 2):
                          </span>
                          <div className="font-bold text-purple-700">
                            ${wsCalc.line2a.toLocaleString()}
                          </div>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <span className="text-purple-600">
                            2b. Sum + Table lookup:
                          </span>
                          <div className="font-bold text-purple-700">
                            ${wsCalc.line2b.toLocaleString()}
                          </div>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <span className="text-purple-600">
                            2c. Total (2a + 2b):
                          </span>
                          <div className="font-bold text-purple-700">
                            ${wsCalc.line2c.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Line 3: Pay Periods */}
                  <div className="space-y-2 p-4 bg-gray-100 rounded-lg">
                    <Label className="text-xs font-semibold uppercase text-gray-600 block">
                      Line 3: Pay Periods Per Year
                    </Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Enter number of pay periods for highest paying job
                      (weekly=52, bi-weekly=26, monthly=12).
                    </p>
                    <Select
                      value={step2Data.payPeriodsPerYear?.toString() || "26"}
                      onValueChange={(val) =>
                        handleChange("payPeriodsPerYear", parseInt(val))
                      }
                      disabled={disabled}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAY_PERIODS.map((pp) => (
                          <SelectItem
                            key={pp.value}
                            value={pp.value.toString()}
                          >
                            {pp.label} ({pp.value} periods)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-xs font-medium text-gray-600">
                        Line 3 value:
                      </span>
                      <span className="text-lg font-bold text-gray-700">
                        {step2Data.payPeriodsPerYear || 26}
                      </span>
                    </div>
                  </div>

                  {/* Line 4: Final Result */}
                  <div className="p-4 bg-green-100 rounded-lg border-2 border-green-300">
                    <Label className="text-xs font-semibold uppercase text-green-700 block mb-2">
                      Line 4: Extra Withholding Per Pay Period
                    </Label>
                    <p className="text-xs text-green-600 mb-3">
                      = (Line 1 or Line 2c) ÷ Line 3. Enter this in Step 4(c).
                    </p>
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-sm font-medium text-green-900 block">
                          Calculation: ${wsCalc.line3.toLocaleString()} ÷{" "}
                          {step2Data.payPeriodsPerYear || 26}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-green-700">
                          ${line4Result.toLocaleString()}
                        </span>
                        <Button
                          size="sm"
                          onClick={handleApplyResult}
                          disabled={line4Result === 0}
                        >
                          Apply to 4(c)
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// STEP 3: CLAIM DEPENDENT AND OTHER CREDITS (2026)
// ============================================================================

// ============================================================================
// STEP 3: CLAIM DEPENDENT AND OTHER CREDITS (2026)
// ============================================================================

function W4Step3({ formData, onChange, errors = {}, disabled = false }) {
  const handleChange = (field, value) => {
    onChange({ ...formData, step3: { ...formData.step3, [field]: value } });
  };

  const step3Data = formData.step3 || {};

  // Calculate total credits using 2026 values
  const childrenUnder17 = parseInt(step3Data.childrenUnder17 || 0);
  const otherDependents = parseInt(step3Data.otherDependents || 0);
  const otherCredits = parseFloat(step3Data.otherCredits || 0);
  const childTaxCredit = childrenUnder17 * CHILD_TAX_CREDIT;
  const otherDependentsCredit = otherDependents * OTHER_DEPENDENT_CREDIT;
  const totalCredits = childTaxCredit + otherDependentsCredit + otherCredits;

  return (
    <Card className="border-gray-200 shadow-none">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <Baby className="h-4 w-4 text-green-600" />
          </div>
          <div>
            <CardTitle className="text-base">
              Step 3: Dependents and Other Credits
            </CardTitle>
            <CardDescription>
              Complete this step if your total income is $200,000 or less
              ($400,000 or less if married filing jointly). This counts as a tax
              credit.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Simple Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Number of children under age 17
            </Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min="0"
                value={step3Data.childrenUnder17 || ""}
                onChange={(e) =>
                  handleChange("childrenUnder17", e.target.value)
                }
                placeholder="0"
                disabled={disabled}
                className="w-24"
              />
              <span className="text-xs text-gray-500">
                x ${CHILD_TAX_CREDIT.toLocaleString()} ={" "}
                <strong>${childTaxCredit.toLocaleString()}</strong>
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Number of other dependents
            </Label>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min="0"
                value={step3Data.otherDependents || ""}
                onChange={(e) =>
                  handleChange("otherDependents", e.target.value)
                }
                placeholder="0"
                disabled={disabled}
                className="w-24"
              />
              <span className="text-xs text-gray-500">
                x ${OTHER_DEPENDENT_CREDIT} ={" "}
                <strong>${otherDependentsCredit.toLocaleString()}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Other Credits Toggle */}
        <div className="pt-2">
          <Label className="text-sm font-medium mb-2 block">
            Other Credits (Optional)
          </Label>
          <CurrencyInput
            value={step3Data.otherCredits}
            onChange={(val) => handleChange("otherCredits", val)}
            placeholder="$0"
            disabled={disabled}
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter other credits you expect to claim (e.g., education, foreign
            tax credit)
          </p>
        </div>

        {/* Total (Subtle) */}
        <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-100">
          <span className="text-sm font-medium text-green-900">
            Total Step 3 Credits
          </span>
          <span className="text-lg font-bold text-green-700">
            ${totalCredits.toLocaleString()}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// STEP 4: OTHER ADJUSTMENTS (2026)
// ============================================================================

function W4Step4({ formData, onChange, errors = {}, disabled = false }) {
  const handleChange = (field, value) => {
    onChange({ ...formData, step4: { ...formData.step4, [field]: value } });
  };

  const step4Data = formData.step4 || {};
  const filingStatus = formData.filingStatus || "single";
  const isMarriedJointly = filingStatus === "married_jointly";
  const isMarriedSeparately =
    filingStatus === "single" && formData.marriedFilingSeparately;

  const [showDeductionsHelp, setShowDeductionsHelp] = useState(false);

  // Get standard deduction based on filing status
  const getStandardDeduction = () => {
    return STANDARD_DEDUCTIONS[filingStatus] || STANDARD_DEDUCTIONS.single;
  };

  // Calculate deductions worksheet totals
  // [Keep calculation logic exactly as is]
  const calculateDeductionsWorksheet = () => {
    const ws = step4Data.deductionsWorksheet || {};
    const line2 =
      (parseFloat(ws.qualifiedTips) || 0) +
      (parseFloat(ws.qualifiedOvertime) || 0) +
      (parseFloat(ws.vehicleLoanInterest) || 0);
    const line4 =
      (parseFloat(ws.seniorDeduction) || 0) +
      (parseFloat(ws.spouseSeniorDeduction) || 0);
    const line7 =
      (parseFloat(ws.medicalExpenses) || 0) +
      (parseFloat(ws.stateLocalTaxes) || 0) +
      (parseFloat(ws.mortgageInterest) || 0) +
      (parseFloat(ws.charitableGifts) || 0) +
      (parseFloat(ws.otherItemized) || 0);
    const line8a = parseFloat(ws.totalIncome) || 0;
    const line8b = Math.max(0, line8a - line4);
    const line9 =
      PHASE_OUT_THRESHOLDS[filingStatus] || PHASE_OUT_THRESHOLDS.single;
    const line10 = line9 > line8b ? line7 : Math.round(line7 * 0.94);
    const line11 = getStandardDeduction();
    const charitableLimit = isMarriedJointly ? 2000 : 1000;
    const line12 = Math.min(
      parseFloat(ws.charitableCashStandard) || 0,
      charitableLimit,
    );
    const line13 = line11 + line12;
    const line14 = line10 > line13 ? line10 - line11 : line12;
    const line5 = parseFloat(ws.otherAdjustments) || 0;
    const line15 = line2 + line4 + line5 + line14;
    return {
      line2,
      line4,
      line5,
      line7,
      line8a,
      line8b,
      line9,
      line10,
      line11,
      line12,
      line13,
      line14,
      line15,
    };
  };

  const wsCalc = calculateDeductionsWorksheet();

  // Store calculated worksheet values in form data for PDF export
  useEffect(() => {
    const currentWs = step4Data.deductionsWorksheet || {};
    // Only update if calculated values changed
    if (
      currentWs.calculatedLine2 !== wsCalc.line2 ||
      currentWs.calculatedLine4 !== wsCalc.line4 ||
      currentWs.calculatedLine7 !== wsCalc.line7 ||
      currentWs.calculatedLine8b !== wsCalc.line8b ||
      currentWs.calculatedLine10 !== wsCalc.line10 ||
      currentWs.calculatedLine11 !== wsCalc.line11 ||
      currentWs.calculatedLine13 !== wsCalc.line13 ||
      currentWs.calculatedLine14 !== wsCalc.line14 ||
      currentWs.calculatedLine15 !== wsCalc.line15
    ) {
      onChange({
        ...formData,
        step4: {
          ...step4Data,
          deductionsWorksheet: {
            ...currentWs,
            // Store calculated values
            calculatedLine2: wsCalc.line2,
            calculatedLine4: wsCalc.line4,
            calculatedLine7: wsCalc.line7,
            calculatedLine8b: wsCalc.line8b,
            calculatedLine9: wsCalc.line9,
            calculatedLine10: wsCalc.line10,
            calculatedLine11: wsCalc.line11,
            calculatedLine13: wsCalc.line13,
            calculatedLine14: wsCalc.line14,
            calculatedLine15: wsCalc.line15,
          },
        },
      });
    }
  }, [wsCalc.line2, wsCalc.line4, wsCalc.line7, wsCalc.line15]);

  return (
    <Card className="border-gray-200 shadow-none">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
            <Calculator className="h-4 w-4 text-purple-600" />
          </div>
          <div>
            <CardTitle className="text-base">
              Step 4: Other Adjustments (Optional)
            </CardTitle>
            <CardDescription>
              Enter other income, deductions, or extra withholding.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 4(a) Other Income */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            4(a) Other income (not from jobs)
          </Label>
          <CurrencyInput
            value={step4Data.otherIncome}
            onChange={(val) => handleChange("otherIncome", val)}
            placeholder="$0"
            disabled={disabled}
          />
          <p className="text-xs text-gray-500">
            Interest, dividends, retirement income, etc. that won't have
            withholding.
          </p>
        </div>

        {/* 4(b) Deductions */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">4(b) Deductions</Label>
            <Button
              variant="link"
              className="h-auto p-0 text-xs text-blue-600"
              onClick={() => setShowDeductionsHelp(!showDeductionsHelp)}
            >
              {showDeductionsHelp
                ? "Hide Calculation Helper"
                : "Help me calculate this"}
            </Button>
          </div>

          <CurrencyInput
            value={
              step4Data.deductions || (showDeductionsHelp ? wsCalc.line15 : 0)
            }
            onChange={(val) => handleChange("deductions", val)}
            placeholder="$0"
            disabled={disabled}
          />
          <p className="text-xs text-gray-500">
            Use this to reduce withholding if you expect to claim deductions
            (e.g. mortgage interest) other than the standard deduction.
          </p>

          {/* Hidden Worksheet - Complete IRS Deductions Worksheet */}
          {showDeductionsHelp && (
            <div className="mt-4 border rounded-lg p-4 bg-gray-50 space-y-6 animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900 border-b pb-2">
                <Calculator className="h-4 w-4" />
                Deductions Worksheet (2026) - Complete IRS Form
              </div>

              {/* Lines 1a-1c: New Tax Benefits */}
              <div className="space-y-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <Label className="text-xs font-semibold uppercase text-green-700 block">
                  Lines 1a-1c: New 2026 Tax Benefits
                </Label>
                <p className="text-xs text-green-600 mb-2">
                  These benefits apply if your income is below certain
                  thresholds.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">
                      1a. Qualified Tips (up to $25,000)
                    </Label>
                    <CurrencyInput
                      value={step4Data.deductionsWorksheet?.qualifiedTips}
                      onChange={(v) =>
                        handleChange("deductionsWorksheet", {
                          ...step4Data.deductionsWorksheet,
                          qualifiedTips: Math.min(parseFloat(v) || 0, 25000),
                        })
                      }
                      placeholder="$0"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      1b. Qualified Overtime (up to $
                      {isMarriedJointly ? "25,000" : "12,500"})
                    </Label>
                    <CurrencyInput
                      value={step4Data.deductionsWorksheet?.qualifiedOvertime}
                      onChange={(v) =>
                        handleChange("deductionsWorksheet", {
                          ...step4Data.deductionsWorksheet,
                          qualifiedOvertime: Math.min(
                            parseFloat(v) || 0,
                            isMarriedJointly ? 25000 : 12500,
                          ),
                        })
                      }
                      placeholder="$0"
                    />
                    <p className="text-xs text-gray-400">
                      Income limit: ${isMarriedJointly ? "300,000" : "150,000"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      1c. Vehicle Loan Interest (up to $10,000)
                    </Label>
                    <CurrencyInput
                      value={step4Data.deductionsWorksheet?.vehicleLoanInterest}
                      onChange={(v) =>
                        handleChange("deductionsWorksheet", {
                          ...step4Data.deductionsWorksheet,
                          vehicleLoanInterest: Math.min(
                            parseFloat(v) || 0,
                            10000,
                          ),
                        })
                      }
                      placeholder="$0"
                    />
                    <p className="text-xs text-gray-400">
                      Income limit: ${isMarriedJointly ? "200,000" : "100,000"}
                    </p>
                  </div>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-green-200">
                  <span className="text-xs font-medium text-green-700">
                    Line 2 Total (1a + 1b + 1c):
                  </span>
                  <span className="text-sm font-bold text-green-700">
                    ${wsCalc.line2.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Lines 3a-3b: Senior Deductions */}
              <div className="space-y-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Label className="text-xs font-semibold uppercase text-purple-700 block">
                  Lines 3a-3b: Senior Deductions (Age 65+)
                </Label>
                <p className="text-xs text-purple-600 mb-2">
                  If income is less than $
                  {isMarriedJointly ? "150,000" : "75,000"}, enter $6,000 for
                  each person age 65+.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="isSenior"
                        checked={
                          step4Data.deductionsWorksheet?.isSenior || false
                        }
                        onCheckedChange={(checked) =>
                          handleChange("deductionsWorksheet", {
                            ...step4Data.deductionsWorksheet,
                            isSenior: checked,
                            seniorDeduction: checked ? 6000 : 0,
                          })
                        }
                      />
                      <Label htmlFor="isSenior" className="text-xs">
                        3a. I am age 65 or older
                      </Label>
                    </div>
                    {step4Data.deductionsWorksheet?.isSenior && (
                      <div className="ml-6 text-xs text-purple-600 font-medium">
                        + $6,000 deduction
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="isSpouseSenior"
                        checked={
                          step4Data.deductionsWorksheet?.isSpouseSenior || false
                        }
                        onCheckedChange={(checked) =>
                          handleChange("deductionsWorksheet", {
                            ...step4Data.deductionsWorksheet,
                            isSpouseSenior: checked,
                            spouseSeniorDeduction: checked ? 6000 : 0,
                          })
                        }
                        disabled={!isMarriedJointly}
                      />
                      <Label
                        htmlFor="isSpouseSenior"
                        className={`text-xs ${
                          !isMarriedJointly ? "text-gray-400" : ""
                        }`}
                      >
                        3b. My spouse is age 65 or older
                      </Label>
                    </div>
                    {step4Data.deductionsWorksheet?.isSpouseSenior &&
                      isMarriedJointly && (
                        <div className="ml-6 text-xs text-purple-600 font-medium">
                          + $6,000 deduction
                        </div>
                      )}
                  </div>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-purple-200">
                  <span className="text-xs font-medium text-purple-700">
                    Line 4 Total (3a + 3b):
                  </span>
                  <span className="text-sm font-bold text-purple-700">
                    ${wsCalc.line4.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Line 5: Other Adjustments */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase text-gray-500 block">
                  Line 5: Other Adjustments (Schedule 1)
                </Label>
                <CurrencyInput
                  value={step4Data.deductionsWorksheet?.otherAdjustments}
                  onChange={(v) =>
                    handleChange("deductionsWorksheet", {
                      ...step4Data.deductionsWorksheet,
                      otherAdjustments: v,
                    })
                  }
                  placeholder="$0"
                />
                <p className="text-xs text-gray-500">
                  Student loan interest, deductible IRA contributions, educator
                  expenses, alimony paid, etc.
                </p>
              </div>

              {/* Lines 6a-6e: Itemized Deductions */}
              <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="text-xs font-semibold uppercase text-blue-700 block">
                  Lines 6a-6e: Itemized Deductions (Schedule A)
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">
                      6a. Medical Expenses (&gt;7.5% of income)
                    </Label>
                    <CurrencyInput
                      value={step4Data.deductionsWorksheet?.medicalExpenses}
                      onChange={(v) =>
                        handleChange("deductionsWorksheet", {
                          ...step4Data.deductionsWorksheet,
                          medicalExpenses: v,
                        })
                      }
                      placeholder="$0"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      6b. State & Local Taxes (up to $
                      {isMarriedSeparately ? "20,200" : "40,400"})
                    </Label>
                    <CurrencyInput
                      value={step4Data.deductionsWorksheet?.stateLocalTaxes}
                      onChange={(v) =>
                        handleChange("deductionsWorksheet", {
                          ...step4Data.deductionsWorksheet,
                          stateLocalTaxes: Math.min(
                            parseFloat(v) || 0,
                            isMarriedSeparately ? 20200 : 40400,
                          ),
                        })
                      }
                      placeholder="$0"
                    />
                    <p className="text-xs text-gray-400">
                      Income limit: $
                      {isMarriedSeparately ? "252,500" : "505,000"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">6c. Mortgage Interest</Label>
                    <CurrencyInput
                      value={step4Data.deductionsWorksheet?.mortgageInterest}
                      onChange={(v) =>
                        handleChange("deductionsWorksheet", {
                          ...step4Data.deductionsWorksheet,
                          mortgageInterest: v,
                        })
                      }
                      placeholder="$0"
                    />
                    <p className="text-xs text-gray-400">
                      Debt limit: ${isMarriedSeparately ? "375,000" : "750,000"}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      6d. Charitable Gifts (&gt;0.5% of income)
                    </Label>
                    <CurrencyInput
                      value={step4Data.deductionsWorksheet?.charitableGifts}
                      onChange={(v) =>
                        handleChange("deductionsWorksheet", {
                          ...step4Data.deductionsWorksheet,
                          charitableGifts: v,
                        })
                      }
                      placeholder="$0"
                    />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label className="text-xs">
                      6e. Other Itemized Deductions
                    </Label>
                    <CurrencyInput
                      value={step4Data.deductionsWorksheet?.otherItemized}
                      onChange={(v) =>
                        handleChange("deductionsWorksheet", {
                          ...step4Data.deductionsWorksheet,
                          otherItemized: v,
                        })
                      }
                      placeholder="$0"
                    />
                    <p className="text-xs text-gray-500">
                      Casualty losses, gambling losses, other qualifying
                      deductions
                    </p>
                  </div>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                  <span className="text-xs font-medium text-blue-700">
                    Line 7 Total (6a + 6b + 6c + 6d + 6e):
                  </span>
                  <span className="text-sm font-bold text-blue-700">
                    ${wsCalc.line7.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Lines 8-10: Itemized Deduction Limitation */}
              <div className="space-y-2 p-3 bg-gray-100 rounded-lg">
                <Label className="text-xs font-semibold uppercase text-gray-600 block">
                  Lines 8-10: Itemized Deduction Limitation
                </Label>
                <div className="space-y-1">
                  <Label className="text-xs">8a. Total Annual Income</Label>
                  <CurrencyInput
                    value={step4Data.deductionsWorksheet?.totalIncome}
                    onChange={(v) =>
                      handleChange("deductionsWorksheet", {
                        ...step4Data.deductionsWorksheet,
                        totalIncome: v,
                      })
                    }
                    placeholder="$0"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs pt-2">
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-500">8b. Income - Line 4:</span>
                    <div className="font-medium">
                      ${wsCalc.line8b.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-500">9. Threshold:</span>
                    <div className="font-medium">
                      ${wsCalc.line9.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-500">10. Allowed:</span>
                    <div className="font-medium">
                      ${wsCalc.line10.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Lines 11-14: Standard vs Itemized */}
              <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Label className="text-xs font-semibold uppercase text-amber-700 block">
                  Lines 11-14: Standard vs Itemized Comparison
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <span className="text-xs text-gray-500">
                      11. Your Standard Deduction:
                    </span>
                    <div className="text-lg font-bold text-gray-700">
                      ${wsCalc.line11.toLocaleString()}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      12. Cash Gifts (Standard Deduction Only, up to $
                      {isMarriedJointly ? "2,000" : "1,000"})
                    </Label>
                    <CurrencyInput
                      value={
                        step4Data.deductionsWorksheet?.charitableCashStandard
                      }
                      onChange={(v) =>
                        handleChange("deductionsWorksheet", {
                          ...step4Data.deductionsWorksheet,
                          charitableCashStandard: Math.min(
                            parseFloat(v) || 0,
                            isMarriedJointly ? 2000 : 1000,
                          ),
                        })
                      }
                      placeholder="$0"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-500">
                      13. Standard + Cash Gifts:
                    </span>
                    <div className="font-medium">
                      ${wsCalc.line13.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-500">
                      14. Additional Deduction:
                    </span>
                    <div className="font-medium">
                      ${wsCalc.line14.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Line 15: Final Result */}
              <div className="bg-blue-100 p-4 rounded-lg flex justify-between items-center border-2 border-blue-300">
                <div>
                  <span className="text-sm font-bold text-blue-900 block">
                    Line 15: Total Deductions for Step 4(b)
                  </span>
                  <span className="text-xs text-blue-700">
                    = Line 2 + Line 4 + Line 5 + Line 14
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-blue-700">
                    ${wsCalc.line15.toLocaleString()}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => handleChange("deductions", wsCalc.line15)}
                  >
                    Use This
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 4(c) Extra Withholding */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">4(c) Extra withholding</Label>
          <CurrencyInput
            value={step4Data.extraWithholding}
            onChange={(val) => handleChange("extraWithholding", val)}
            placeholder="$0"
            disabled={disabled}
          />
          <p className="text-xs text-gray-500">
            Any additional tax you want withheld each pay period (including from
            Step 2).
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================

// ============================================================================
// MAIN FORM W-4 COMPONENT
// ============================================================================

export default function FormW4({
  employee,
  existingSubmission,
  onSave,
  onSubmit,
  disabled = false,
  className = "",
  isAdmin = false, // Whether the user is an admin who can fill employer section
  companyInfo = null, // Company information for employer section
}) {
  // Get profile data for pre-population
  const getProfileData = () => {
    const profile = employee?.profile || {};
    const address = profile.address?.mailing || {};
    return {
      firstName: profile.firstName || employee?.firstName || "",
      lastName: profile.lastName || employee?.lastName || "",
      middleInitial: profile.middleInitial || "",
      address: address.address1 || "",
      addressLine2: address.address2 || "",
      city: address.city || "",
      state: address.state || "",
      zipCode: address.zip || "",
    };
  };

  const profileData = getProfileData();

  // Start at step 5 if admin, otherwise step 1
  const [currentStep, setCurrentStep] = useState(isAdmin ? 5 : 1);
  const [formData, setFormData] = useState(
    existingSubmission || {
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      middleInitial: profileData.middleInitial,
      ssn: "",
      address: profileData.address,
      addressLine2: profileData.addressLine2,
      city: profileData.city,
      state: profileData.state,
      zipCode: profileData.zipCode,
      filingStatus: "",
      claimExempt: false,
      step2: {
        usedEstimator: false,
        twoJobsOnly: false,
        hasThreeOrMoreJobs: false,
        higherJobWages: 0,
        lowerJobWages: 0,
        job1Wages: 0,
        job2Wages: 0,
        job3Wages: 0,
        payPeriodsPerYear: 26,
        twoJobsAmount: 0,
        line2a: 0,
        line2b: 0,
        line2c: 0,
        line4Result: 0,
      },
      step3: {
        childrenUnder17: 0,
        otherDependents: 0,
        otherCredits: 0,
      },
      step4: {
        otherIncome: 0,
        deductions: 0,
        extraWithholding: 0,
        deductionsWorksheet: {
          qualifiedTips: 0,
          qualifiedOvertime: 0,
          vehicleLoanInterest: 0,
          isSenior: false,
          seniorDeduction: 0,
          isSpouseSenior: false,
          spouseSeniorDeduction: 0,
          otherAdjustments: 0,
          medicalExpenses: 0,
          stateLocalTaxes: 0,
          mortgageInterest: 0,
          charitableGifts: 0,
          otherItemized: 0,
          totalIncome: 0,
          charitableCashStandard: 0,
        },
      },
      employer: companyInfo || {},
    },
  );
  const [errors, setErrors] = useState({});
  const [showSignature, setShowSignature] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  // Employee sees it as complete if it's pending review. Admin sees it as incomplete so they can edit step 5.
  const [isComplete, setIsComplete] = useState(
    existingSubmission?.status === "complete" ||
      (!isAdmin && existingSubmission?.status === "pending_employer_review"),
  );

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.ssn) newErrors.ssn = "Social Security Number is required";
    if (!formData.address) newErrors.address = "Address is required";
    if (!formData.city) newErrors.city = "City is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.zipCode) newErrors.zipCode = "ZIP Code is required";
    if (!formData.filingStatus)
      newErrors.filingStatus = "Filing status is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate filled PDF using template
  const handleGeneratePdf = async () => {
    setIsGeneratingPdf(true);
    try {
      // Import the new template-based PDF generator
      const { generateW4PdfFromTemplate } =
        await import("@/lib/w4PdfGenerator");

      // Generate the filled PDF using the template (uses fw4.pdf from public folder)
      const pdfBlob = await generateW4PdfFromTemplate(formData, "/fw4.pdf");

      // Download the PDF
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `W4_${formData.firstName}_${formData.lastName}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("W-4 PDF generated successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(`PDF Generation Error: ${error.message}`);
      // Fallback disabled for debugging
      // try {
      //   await generateW4Preview(formData);
      // } catch (previewError) {
      //   toast.error('Failed to generate PDF. Please try again.');
      // }
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Generate a printable HTML preview as fallback
  const generateW4Preview = async (data) => {
    // Calculate credits
    const childrenUnder17 = parseInt(data.step3?.childrenUnder17 || 0);
    const otherDependents = parseInt(data.step3?.otherDependents || 0);
    const totalCredits = childrenUnder17 * 2000 + otherDependents * 500;

    const formatCurrency = (val) => {
      if (!val) return "";
      return parseFloat(val).toLocaleString("en-US");
    };

    const formatDate = (date) => {
      if (!date) return "";
      const d = new Date(date);
      return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
        d.getDate(),
      ).padStart(2, "0")}/${d.getFullYear()}`;
    };

    // Create a printable HTML document
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Form W-4 - ${data.firstName} ${data.lastName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11pt; 
            line-height: 1.3;
            padding: 20px;
            max-width: 8.5in;
            margin: 0 auto;
          }
          .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 15px; }
          .header h1 { font-size: 16pt; margin-bottom: 5px; }
          .header p { font-size: 9pt; color: #666; }
          .form-number { position: absolute; top: 20px; left: 20px; font-weight: bold; }
          .section { margin-bottom: 15px; border: 1px solid #000; padding: 10px; }
          .section-title { font-weight: bold; background: #f0f0f0; padding: 5px; margin: -10px -10px 10px; font-size: 10pt; }
          .row { display: flex; margin-bottom: 8px; }
          .field { flex: 1; margin-right: 10px; }
          .field:last-child { margin-right: 0; }
          .field label { display: block; font-size: 8pt; color: #666; margin-bottom: 2px; }
          .field .value { border-bottom: 1px solid #000; min-height: 20px; padding: 2px 5px; }
          .checkbox-row { display: flex; align-items: center; margin: 5px 0; }
          .checkbox { width: 12px; height: 12px; border: 1px solid #000; margin-right: 8px; display: flex; align-items: center; justify-content: center; }
          .checkbox.checked::after { content: "✓"; font-weight: bold; }
          .money-field .value { text-align: right; }
          .signature-line { margin-top: 20px; }
          .signature-line .value { min-height: 40px; }
          .employer-section { background: #fffde7; }
          .instructions { font-size: 8pt; color: #666; margin: 10px 0; }
          @media print {
            body { padding: 0; }
            .section { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="form-number">Form W-4</div>
          <h1>Employee's Withholding Certificate</h1>
          <p>Department of the Treasury • Internal Revenue Service • 2026</p>
        </div>

        <div class="section">
          <div class="section-title">Step 1: Enter Personal Information</div>
          <div class="row">
            <div class="field" style="flex: 2;">
              <label>(a) First name and middle initial</label>
              <div class="value">${data.firstName || ""} ${
                data.middleInitial || ""
              }</div>
            </div>
            <div class="field" style="flex: 2;">
              <label>Last name</label>
              <div class="value">${data.lastName || ""}</div>
            </div>
            <div class="field">
              <label>(b) Social security number</label>
              <div class="value">${data.ssn || ""}</div>
            </div>
          </div>
          <div class="row">
            <div class="field">
              <label>(c) Address (number and street)</label>
              <div class="value">${data.address || ""} ${
                data.addressLine2 || ""
              }</div>
            </div>
          </div>
          <div class="row">
            <div class="field">
              <label>City or town, state, and ZIP code</label>
              <div class="value">${data.city || ""}, ${data.state || ""} ${
                data.zipCode || ""
              }</div>
            </div>
          </div>
          <div style="margin-top: 10px;">
            <label style="font-size: 8pt; color: #666;">(c) Filing Status - Check only one box:</label>
            <div class="checkbox-row">
              <div class="checkbox ${
                data.filingStatus === "single" ? "checked" : ""
              }"></div>
              <span>Single or Married filing separately</span>
            </div>
            <div class="checkbox-row">
              <div class="checkbox ${
                data.filingStatus === "married_jointly" ? "checked" : ""
              }"></div>
              <span>Married filing jointly</span>
            </div>
            <div class="checkbox-row">
              <div class="checkbox ${
                data.filingStatus === "head_of_household" ? "checked" : ""
              }"></div>
              <span>Head of household</span>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Step 2: Multiple Jobs or Spouse Works (Optional)</div>
          <p class="instructions">Complete this step if you hold more than one job or are married filing jointly and your spouse also works.</p>
          <div class="checkbox-row">
            <div class="checkbox ${
              data.step2?.twoJobsOnly ? "checked" : ""
            }"></div>
            <span>(c) If there are only two jobs total, check this box</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Step 3: Claim Dependents</div>
          <p class="instructions">If your total income will be $200,000 or less ($400,000 or less if married filing jointly):</p>
          <div class="row">
            <div class="field money-field">
              <label>Qualifying children under 17 (× $2,000): ${childrenUnder17} × $2,000</label>
              <div class="value">$${(
                childrenUnder17 * 2000
              ).toLocaleString()}</div>
            </div>
            <div class="field money-field">
              <label>Other dependents (× $500): ${otherDependents} × $500</label>
              <div class="value">$${(
                otherDependents * 500
              ).toLocaleString()}</div>
            </div>
          </div>
          <div class="row">
            <div class="field money-field" style="flex: 0 0 200px; margin-left: auto;">
              <label>3. Total (Add amounts above)</label>
              <div class="value" style="font-weight: bold;">$${totalCredits.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Step 4: Other Adjustments (Optional)</div>
          <div class="row">
            <div class="field money-field">
              <label>(a) Other income (not from jobs)</label>
              <div class="value">${
                data.step4?.otherIncome
                  ? "$" + formatCurrency(data.step4.otherIncome)
                  : ""
              }</div>
            </div>
            <div class="field money-field">
              <label>(b) Deductions</label>
              <div class="value">${
                data.step4?.deductions
                  ? "$" + formatCurrency(data.step4.deductions)
                  : ""
              }</div>
            </div>
            <div class="field money-field">
              <label>(c) Extra withholding</label>
              <div class="value">${
                data.step4?.extraWithholding
                  ? "$" + formatCurrency(data.step4.extraWithholding)
                  : ""
              }</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Step 5: Sign Here</div>
          <p class="instructions">Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.</p>
          <div class="row signature-line">
            <div class="field" style="flex: 2;">
              <label>Employee's signature</label>
              <div class="value">${
                existingSubmission?.signature?.typedName || ""
              }</div>
            </div>
            <div class="field">
              <label>Date</label>
              <div class="value">${formatDate(
                existingSubmission?.signature?.signedAt ||
                  existingSubmission?.submittedAt,
              )}</div>
            </div>
          </div>
        </div>

        <div class="section employer-section">
          <div class="section-title">Employers Only</div>
          <div class="row">
            <div class="field" style="flex: 2;">
              <label>Employer's name and address</label>
              <div class="value">${data.employer?.companyName || ""}<br>${
                data.employer?.address || ""
              } ${data.employer?.addressLine2 || ""}<br>${data.employer?.city || ""}, ${
                data.employer?.state || ""
              } ${data.employer?.zipCode || ""}</div>
            </div>
            <div class="field">
              <label>First date of employment</label>
              <div class="value">${formatDate(
                data.employer?.firstDateOfEmployment,
              )}</div>
            </div>
            <div class="field">
              <label>Employer identification number (EIN)</label>
              <div class="value">${data.employer?.ein || ""}</div>
            </div>
          </div>
        </div>

        <script>
          window.onload = function() { window.print(); };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    toast.success("W-4 form opened for printing");
  };

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const pdfTemplateFields = mapFormDataToTemplateFields(formData);
      await onSave?.({
        ...formData,
        pdfTemplateFields,
        status: "draft",
        updatedAt: new Date().toISOString(),
      });
      toast.success("W-4 form saved as draft");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle submit
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!validateForm()) {
      setCurrentStep(1);
      return;
    }
    setShowSignature(true);
  };

  // Handle signature
  const handleSigned = async (signatureData) => {
    setIsSubmitting(true);
    try {
      // Create the complete submission data including signature
      const signedFormData = {
        ...formData,
        signature: signatureData,
        status: "pending_employer_review",
        submittedAt: new Date().toISOString(),
      };

      // Generate template fields WITH the signature
      const pdfTemplateFields = mapFormDataToTemplateFields(
        signedFormData,
        signatureData,
      );

      // Update local state so UI reflects signed status
      setFormData(signedFormData);

      await onSubmit?.({
        ...signedFormData,
        pdfTemplateFields,
      });

      setIsComplete(true);
      setShowSignature(false);
      toast.success("W-4 submitted. Pending employer review.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: "Personal Info", required: true },
    { number: 2, title: "Multiple Jobs", required: false },
    { number: 3, title: "Dependents", required: false },
    { number: 4, title: "Adjustments", required: false },
    { number: 5, title: "Employer Info", required: false, adminOnly: true },
  ];

  // Filter steps based on admin status
  const visibleSteps = isAdmin ? steps : steps.filter((s) => !s.adminOnly);
  const maxStep = visibleSteps.length;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            Form W-4: Employee's Withholding Certificate
          </h2>
          <p className="text-gray-600 mt-1">
            Department of the Treasury • Internal Revenue Service • 2026
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isComplete && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGeneratePdf}
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Generating...
                  </>
                ) : (
                  <>
                    <Printer className="h-4 w-4 mr-2" />
                    Print / Download PDF
                  </>
                )}
              </Button>
              <Badge className="bg-green-100 text-green-800 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Completed
              </Badge>
            </>
          )}
        </div>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            {visibleSteps.map((step, index) => (
              <React.Fragment key={step.number}>
                <button
                  onClick={() => setCurrentStep(step.number)}
                  disabled={isComplete}
                  className={`flex flex-col items-center gap-2 ${
                    currentStep === step.number
                      ? "text-blue-600"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                      currentStep === step.number
                        ? "border-blue-600 bg-blue-100"
                        : currentStep > step.number
                          ? "border-green-600 bg-green-100 text-green-600"
                          : "border-gray-300"
                    } ${step.adminOnly ? "border-amber-500" : ""}`}
                  >
                    {currentStep > step.number ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : step.adminOnly ? (
                      <Building2 className="h-4 w-4 text-amber-600" />
                    ) : (
                      step.number
                    )}
                  </div>
                  <span className="text-xs font-medium">{step.title}</span>
                  {!step.required && (
                    <span className="text-xs text-gray-400">(Optional)</span>
                  )}
                </button>
                {index < visibleSteps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      currentStep > step.number ? "bg-green-500" : "bg-gray-200"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Instructions</AlertTitle>
        <AlertDescription className="text-blue-700">
          <strong>Step 1</strong> is required. <strong>Steps 2-4</strong> are
          optional but will help ensure that your federal income tax withholding
          matches your tax liability. Complete Steps 2-4 only if they apply to
          you.
        </AlertDescription>
      </Alert>

      {/* Form Steps */}
      {!isComplete && (
        <>
          {currentStep === 1 && (
            <W4Step1
              formData={formData}
              onChange={setFormData}
              errors={errors}
              disabled={disabled}
            />
          )}
          {currentStep === 2 && (
            <W4Step2
              formData={formData}
              onChange={setFormData}
              errors={errors}
              disabled={disabled}
            />
          )}
          {currentStep === 3 && (
            <W4Step3
              formData={formData}
              onChange={setFormData}
              errors={errors}
              disabled={disabled}
            />
          )}
          {currentStep === 4 && (
            <W4Step4
              formData={formData}
              onChange={setFormData}
              errors={errors}
              disabled={disabled}
            />
          )}
          {currentStep === 5 && isAdmin && (
            <W4EmployerSection
              formData={formData}
              onChange={setFormData}
              errors={errors}
              disabled={disabled}
              companyInfo={companyInfo}
            />
          )}
        </>
      )}

      {/* Completed State */}
      {isComplete && existingSubmission?.signature && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle
              className={`flex items-center gap-2 ${
                formData.status === "pending_employer_review"
                  ? "text-blue-800"
                  : "text-green-800"
              }`}
            >
              <CheckCircle2 className="h-5 w-5" />
              {formData.status === "pending_employer_review"
                ? "W-4 Submitted (Pending Employer Review)"
                : "Form W-4 Complete"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Employee Information Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Employee Name:</span>
                <p className="font-medium">
                  {formData.firstName} {formData.middleInitial}{" "}
                  {formData.lastName}
                </p>
              </div>
              <div>
                <span className="text-gray-500">SSN:</span>
                <p className="font-medium">
                  {formData.ssn ? `***-**-${formData.ssn.slice(-4)}` : "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Address:</span>
                <p className="font-medium">
                  {formData.city}, {formData.state} {formData.zipCode}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Filing Status:</span>
                <p className="font-medium">
                  {W4_FILING_STATUS.find(
                    (s) => s.value === formData.filingStatus,
                  )?.label || "Not specified"}
                </p>
              </div>
            </div>

            <Separator />

            {/* Tax Withholding Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Multiple Jobs (Step 2c):</span>
                <p className="font-medium">
                  {formData.step2?.twoJobsOnly ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">
                  Dependents Credit (Step 3):
                </span>
                <p className="font-medium">
                  $
                  {(
                    parseInt(formData.step3?.childrenUnder17 || 0) * 2000 +
                    parseInt(formData.step3?.otherDependents || 0) * 500
                  ).toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Other Income (4a):</span>
                <p className="font-medium">
                  ${formData.step4?.otherIncome || 0}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Extra Withholding (4c):</span>
                <p className="font-medium">
                  ${formData.step4?.extraWithholding || 0}
                </p>
              </div>
            </div>

            {/* Employer Information (if filled) */}
            {formData.employer?.companyName && (
              <>
                <Separator />
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4" />
                    Employer Information
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Employer Name:</span>
                      <p className="font-medium">
                        {formData.employer.companyName}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">EIN:</span>
                      <p className="font-medium">
                        {formData.employer.ein || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Location:</span>
                      <p className="font-medium">
                        {formData.employer.city}, {formData.employer.state}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">
                        First Date of Employment:
                      </span>
                      <p className="font-medium">
                        {formData.employer.firstDateOfEmployment
                          ? new Date(
                              formData.employer.firstDateOfEmployment,
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Separator />
            <SignatureDisplay signature={existingSubmission.signature} />
            <p className="text-sm text-green-700">
              Submitted on{" "}
              {new Date(existingSubmission.submittedAt).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      {!isComplete && (
        <div className="flex justify-between">
          <div className="flex gap-2">
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                Previous
              </Button>
            )}
            <Button variant="outline" onClick={handleSave} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? "Saving..." : "Save Draft"}
            </Button>
          </div>
          <div className="flex gap-2">
            {currentStep < maxStep ? (
              <Button onClick={() => setCurrentStep(currentStep + 1)}>
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                className="bg-green-600 hover:bg-green-700"
              >
                <Send className="h-4 w-4 mr-2" />
                Sign & Submit
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Actions for completed forms */}
      {isComplete && (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleGeneratePdf}
            disabled={isGeneratingPdf}
          >
            {isGeneratingPdf ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Generating...
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" />
                Print / Download PDF
              </>
            )}
          </Button>
        </div>
      )}

      {/* Signature Dialog */}
      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <ESignature
            documentTitle="Form W-4: Employee's Withholding Certificate"
            documentType="form_w4"
            documentId={existingSubmission?.id || "new"}
            signerId={employee?.id}
            signerName={`${formData.firstName} ${formData.lastName}`}
            signerEmail={employee?.email || ""}
            onSign={handleSigned}
            onCancel={() => setShowSignature(false)}
            disabled={isSubmitting}
            consentText={`Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.

I understand that I may be subject to a penalty of $500 if I provide false information on this form with no reasonable basis and the withholding that results is less than the amount that should have been withheld.`}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// W-4 STATUS CARD (For Dashboard)
// ============================================================================

export function W4StatusCard({ submission, employee, onView, onEdit }) {
  const isComplete = submission?.status === "complete";
  const isPendingEmployer = submission?.status === "pending_employer_review";

  return (
    <Card
      className={`transition-all hover:shadow-md ${
        isComplete ? "border-green-200" : ""
      }`}
    >
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isComplete ? "bg-green-100" : "bg-blue-100"
              }`}
            >
              <FileText
                className={`h-5 w-5 ${
                  isComplete ? "text-green-600" : "text-blue-600"
                }`}
              />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Form W-4</h4>
              <p className="text-sm text-gray-500">Federal Tax Withholding</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isComplete ? (
              <Badge className="bg-green-100 text-green-800 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Complete
              </Badge>
            ) : isPendingEmployer ? (
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200"
              >
                Employer Review
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="bg-yellow-50 text-yellow-700 border-yellow-200"
              >
                Pending
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                isComplete || isPendingEmployer
                  ? onView?.(submission)
                  : onEdit?.(submission)
              }
            >
              {isComplete || isPendingEmployer ? "View" : "Complete"}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>

        {isComplete && submission?.submittedAt && (
          <p className="text-xs text-gray-500 mt-3">
            Last updated:{" "}
            {new Date(submission.submittedAt).toLocaleDateString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
